import { useState } from "react";
import { useContent } from "../hooks/useContent";
import { useInView } from "../hooks/useInView";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

export const TestimonialsSection = () => {
  const [ref, isInView] = useInView();
  const { getSection } = useContent();
  const testimonials = getSection("testimonials") || [];
  const [activeIndex, setActiveIndex] = useState(0);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (!testimonials.length) return null;

  return (
    <section id="reviews" className="py-16 lg:py-20 bg-white relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-lime-50/30 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16" ref={ref}>
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            Google Reviews
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-5">
            Loved by Jaipur,
            <br />
            Trusted by Thousands
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Rated 4.9 stars on Google. Here's what our customers say about Blitzwash.
          </p>
        </div>

        <div
          className={`hidden md:grid md:grid-cols-3 gap-6 mb-8 transition-all duration-700 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {testimonials.slice(0, 3).map((t, i) => (
            <div key={i} className="bg-sky-50/40 rounded-2xl p-7 border border-sky-100 hover:border-sky-200 hover:shadow-md transition-all duration-300 relative">
              <Quote className="w-8 h-8 text-sky-100 absolute top-6 right-6" />
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-lime-500 fill-lime-500" />
                ))}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">"{t.text}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-sky-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white text-sm font-bold">
                  {t.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="md:hidden">
          <div className="bg-sky-50/40 rounded-2xl p-7 border border-sky-100 relative">
            <Quote className="w-8 h-8 text-sky-100 absolute top-6 right-6" />
            <div className="flex items-center gap-1 mb-4">
              {Array.from({ length: testimonials[activeIndex].rating }).map((_, j) => (
                <Star key={j} className="w-4 h-4 text-lime-500 fill-lime-500" />
              ))}
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">"{testimonials[activeIndex].text}"</p>
            <div className="flex items-center gap-3 pt-4 border-t border-sky-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white text-sm font-bold">
                {testimonials[activeIndex].avatar}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{testimonials[activeIndex].name}</p>
                <p className="text-xs text-slate-400">{testimonials[activeIndex].location}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-6">
            <button onClick={prevTestimonial} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-sky-50 transition-colors">
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActiveIndex(i)} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === activeIndex ? "bg-sky-600 w-6" : "bg-slate-300"}`} />
              ))}
            </div>
            <button onClick={nextTestimonial} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-sky-50 transition-colors">
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>

        <div
          className={`hidden md:grid md:grid-cols-2 gap-6 max-w-4xl mx-auto transition-all duration-700 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "200ms" }}
        >
          {testimonials.slice(3, 5).map((t, i) => (
            <div key={i} className="bg-sky-50/40 rounded-2xl p-7 border border-sky-100 hover:border-sky-200 hover:shadow-md transition-all duration-300 relative">
              <Quote className="w-8 h-8 text-sky-100 absolute top-6 right-6" />
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-lime-500 fill-lime-500" />
                ))}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">"{t.text}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-sky-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white text-sm font-bold">
                  {t.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
